import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { isEmpty } from 'validate.js';

import Icon from 'dpl/common/components/Icon';
import MiniOverlayModal from 'dpl/common/mini_overlay_modal/components/MiniOverlayModal';
import FixedTooltipPortal from 'dpl/common/tooltip/components/FixedTooltipPortal';
import SmartLink from 'dpl/components/SmartLink';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEW_FEATURE_LOCATIONS,
  ANALYTICS_VIEW_FEATURES
} from 'dpl/constants/analytics';
import {
  LEARNING_CENTER_PAYMENT_PROTECTION_PATH,
  PAYMENT_PROTECTION_POLICY_PATH
} from 'dpl/shared/constants/urls';
import useToggle from 'dpl/shared/hooks/useToggle';
import { capitalize } from 'dpl/shared/utils';
import { sendRudderstackEvent } from 'dpl/util/analytics';
import { isLessThanBreakpoint } from 'dpl/util/grid';

import KlarnaDescription from './KlarnaDescription';

const IS_LT_MD = isLessThanBreakpoint('md');

const DEPOSIT_SECURES_DESCRIPTIONS = {
  specific_puppy: 'This deposit secures a specific puppy.',
  litter_spot: 'This deposit secures a spot in a litter.',
  waitlist_spot: 'This deposit secures a spot on the general waitlist.'
};

const FINAL_PAYMENT_TIMING_DESCRIPTIONS = {
  before_pickup: 'The remaining balance is due before pickup.',
  at_pickup: 'The remaining balance is due at pickup.'
};

/** @param {any} props */
function PriceSection({
  className = null,
  heading,
  subheading = null,
  amount,
  content = null
}) {
  const [isExpanded, toggleIsExpanded] = useToggle(false);

  return (
    <div
      className={classnames(
        'PriceSection flex flex-column gap-16 font-16 pv6',
        className
      )}
    >
      <div className="flex justify-between gap-24 lh-title">
        <div className="flex items-center gap-8 flex-wrap">
          <span className="fw-medium">{heading}</span>
          <div className="flex items-center gap-8">
            {subheading && <span className="tertiary">{subheading}</span>}
            {!isEmpty(content) && (
              <button
                type="button"
                className={classnames(
                  'bg-gray-200 br-100',
                  isExpanded ? 'padding-h-6 padding-v-6' : 'ph1 pv1'
                )}
                style={{ height: '28px', width: '28px' }}
                onClick={toggleIsExpanded}
              >
                <Icon
                  name={isExpanded ? 'fetch-close' : 'fetch-info'}
                  className="secondary"
                  height="100%"
                  width="100%"
                />
              </button>
            )}
          </div>
        </div>
        <span className="fw-medium nowrap">{amount}</span>
      </div>
      {isExpanded && <div className="font-14 tertiary">{content}</div>}
    </div>
  );
}

PriceSection.propTypes = {
  className: PropTypes.string,
  heading: PropTypes.string.isRequired,
  subheading: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  amount: PropTypes.string.isRequired,
  content: PropTypes.node
};

function IncludedExtrasBanner({ paragraphs }) {
  return (
    <div className="IncludedExtrasBanner flex flex-column gap-8 font-14 bg-teal-light br3 pv3 ph3">
      {paragraphs.map((paragraph, index) => (
        <p key={index}>{paragraph}</p>
      ))}
    </div>
  );
}

IncludedExtrasBanner.propTypes = {
  paragraphs: PropTypes.arrayOf(PropTypes.string).isRequired
};

/** @param {any} props */
export function PaymentBanner({
  className = null,
  view = null,
  viewFeatureLocation = ANALYTICS_VIEW_FEATURE_LOCATIONS.PRICE_BREAKDOWN_MODAL,
  onLearnMoreClick = null
}) {
  function trackLearnMoreClick(e) {
    sendRudderstackEvent(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      button_cta: e.target.textContent,
      view_feature: ANALYTICS_VIEW_FEATURES.PAYMENT_POLICY_LINK,
      view_feature_location: viewFeatureLocation,
      view
    });

    if (onLearnMoreClick) {
      e.preventDefault();
      onLearnMoreClick();
    }
  }

  return (
    <div
      className={classnames(
        'PaymentBanner flex items-center gap-12 br3 ba b--default pl3 pr3 pt3 pb3',
        className
      )}
    >
      <div className="flex items-center justify-center bg-gray-200 br3 pv2 ph2">
        <Icon
          className="primary"
          name="gooddog-shield"
          height="24px"
          width="24px"
        />
      </div>
      <p>
        Payments through Good Dog are protected.{' '}
        <SmartLink
          className="fw-medium primary"
          to={LEARNING_CENTER_PAYMENT_PROTECTION_PATH}
          target="_blank"
          onClick={trackLearnMoreClick}
        >
          Learn more
        </SmartLink>
      </p>
    </div>
  );
}

PaymentBanner.propTypes = {
  className: PropTypes.string,
  view: PropTypes.string,
  viewFeatureLocation: PropTypes.string,
  // opens the protection sheet in place instead of following the link
  onLearnMoreClick: PropTypes.func
};

/** @param {any} props */
export function PriceBreakdownContent({
  view = null,
  className = null,
  deposit = null,
  depositDescription = null,
  depositRefundPolicy = null,
  depositSecures = null,
  dogPayment = null,
  finalPaymentTiming = null,
  subtotal,
  isBnplAvailable = false,
  hasProtectionBanner = false,
  includesFees = false
}) {
  const items = useMemo(() => {
    return [
      deposit && {
        heading: 'Deposit',
        amount: deposit,
        subheading: depositRefundPolicy
          ? capitalize(depositRefundPolicy)
          : null,
        content:
          depositSecures || depositDescription ? (
            <div className="flex flex-column gap-8">
              {depositSecures && (
                <p>{DEPOSIT_SECURES_DESCRIPTIONS[depositSecures]}</p>
              )}
              {depositDescription && (
                <p>Breeder’s policy: “{depositDescription}”</p>
              )}
            </div>
          ) : null
      },
      dogPayment && {
        heading: 'Dog payment',
        amount: dogPayment,
        subheading: isBnplAvailable ? <KlarnaDescription as="span" /> : null,
        content:
          deposit && finalPaymentTiming ? (
            <p>{FINAL_PAYMENT_TIMING_DESCRIPTIONS[finalPaymentTiming]}</p>
          ) : null
      },
      subtotal && {
        heading: 'Subtotal',
        amount: subtotal,
        subheading: includesFees ? 'Before taxes' : 'Before taxes & fees',
        content: (
          <>
            State and local taxes are calculated at payment based on your
            puppy’s pickup location.{' '}
            {includesFees
              ? 'These amounts include Good Dog’s fees, which cover processing and platform costs and protect your payment with'
              : 'Fees cover processing and platform costs, and ensure your payment is protected with'}{' '}
            <SmartLink
              className="stone-700 underline"
              to={PAYMENT_PROTECTION_POLICY_PATH}
              target="_blank"
            >
              Good Dog Payment Protection
            </SmartLink>
            .
          </>
        )
      }
    ].filter(Boolean);
  }, []);

  return (
    <div className={classnames('PriceBreakdownContent', className)}>
      <ul>
        {items.map((item, index) => (
          <PriceSection
            key={index}
            className={classnames({ 'bt b--gray-300': index })}
            {...item}
          />
        ))}
        {hasProtectionBanner && (
          <li className="bt b--gray-300">
            <PaymentBanner className="font-16 b--none pv6 ph0" view={view} />
          </li>
        )}
      </ul>
    </div>
  );
}

PriceBreakdownContent.propTypes = {
  className: PropTypes.string,
  deposit: PropTypes.string,
  depositDescription: PropTypes.string,
  depositRefundPolicy: PropTypes.string,
  depositSecures: PropTypes.string,
  dogPayment: PropTypes.string,
  finalPaymentTiming: PropTypes.string,
  subtotal: PropTypes.string.isRequired,
  isBnplAvailable: PropTypes.bool,
  hasProtectionBanner: PropTypes.bool,
  includesFees: PropTypes.bool,
  view: PropTypes.string
};

/** @param {Record<string, any>} props */
export default function PriceBreakdownModal({
  view,
  includedExtrasParagraphs = null,
  isOpen,
  onClose,
  ...contentProps
}) {
  function Heading() {
    return <h3 className="font-24 lh-title">Price breakdown</h3>;
  }

  if (IS_LT_MD) {
    return (
      <FixedTooltipPortal
        className="PriceBreakdownModal pt6"
        isVisible={isOpen}
        onCloseClick={onClose}
      >
        <div
          className="no-scrollbars overflow-y-auto"
          style={{ maxHeight: '80vh' }}
        >
          <Heading />
          <PriceBreakdownContent
            {...contentProps}
            hasProtectionBanner={false}
          />
          {!isEmpty(includedExtrasParagraphs) && (
            <IncludedExtrasBanner paragraphs={includedExtrasParagraphs} />
          )}
          <PaymentBanner className="font-14 mt6" view={view} />
        </div>
      </FixedTooltipPortal>
    );
  }

  if (!isOpen) return null;

  return (
    <MiniOverlayModal
      className="PriceBreakdownModal"
      closeButtonClassName="ba b--light-gray bg-white br-100 padding-h-6 padding-v-6 mt5 mt4-md mr4"
      contentClassName="pv8 ph8 bg-white"
      onCloseClick={onClose}
      maxWidth="600px"
    >
      <Heading />
      <PriceBreakdownContent {...contentProps} hasProtectionBanner={false} />
      {!isEmpty(includedExtrasParagraphs) && (
        <IncludedExtrasBanner paragraphs={includedExtrasParagraphs} />
      )}
      <PaymentBanner className="font-16 hover-bg-gray-200 mt6" view={view} />
    </MiniOverlayModal>
  );
}

PriceBreakdownModal.propTypes = {
  ...PriceBreakdownContent.propTypes,
  includedExtrasParagraphs: PropTypes.arrayOf(PropTypes.string),
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};
